import { useState } from "react";
import { Navigate, Routes, Route, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LogOut, Store, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { StoreBilling } from "@/components/admin/StoreBilling";
import { firebaseReady } from "@/lib/firebase";

const TOASTER_OPTS = {
  position: "top-center",
  toastOptions: {
    style: {
      background: "#F5E6CC",
      color: "#2E1A12",
      border: "1px solid rgba(90,45,29,0.25)",
      borderRadius: 2,
    },
  },
};

const StoreWaitScreen = ({ message = "Loading store…" }) => (
  <div className="min-h-screen bg-[#F5E6CC] grid place-items-center px-4">
    <div className="text-center">
      <Loader2 className="w-8 h-8 text-[#556B2F] animate-spin mx-auto mb-3" />
      <p className="text-sm font-semibold text-[#5A2D1D]">{message}</p>
    </div>
  </div>
);

const StoreBar = ({ onAuthOpen }) => {
  const { user, profile, signOut } = useAuth();
  const initials = profile?.name
    ? profile.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() || "S";

  return (
    <header className="sticky top-0 z-40 bg-[#556B2F]/95 backdrop-blur-md border-b border-[#556B2F] text-[#F5E6CC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5" />
            <span className="font-display text-sm font-bold">
              Annapurna <span className="opacity-80">Store</span>
            </span>
          </div>
          {user ? (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-[#F5E6CC] text-[#556B2F] grid place-items-center text-[10px] font-bold">
                {initials}
              </div>
              <span className="text-xs font-semibold max-w-[140px] truncate hidden sm:inline">
                {profile?.name || (profile?.store_user_id ? `ID ${profile.store_user_id}` : "Store")}
              </span>
              <button
                type="button"
                onClick={signOut}
                className="inline-flex items-center gap-1 text-xs font-semibold border border-[#F5E6CC]/40 px-2.5 py-1 rounded-full hover:bg-[#F5E6CC]/10"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign Out
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onAuthOpen}
              className="text-xs font-bold border border-[#F5E6CC]/40 px-3 py-1.5 rounded-full hover:bg-[#F5E6CC]/10"
            >
              Sign In
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export const StoreAssociateShell = () => {
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const { user, isStoreAssociate, loading, signOut } = useAuth();

  if (loading) return <StoreWaitScreen message="Signing you in…" />;

  if (!firebaseReady) {
    return (
      <div className="min-h-screen bg-[#F5E6CC] grid place-items-center px-4 text-center">
        <p className="text-[#5A2D1D] font-semibold">Store billing is unavailable — Supabase is not connected.</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F5E6CC]">
        <StoreBar onAuthOpen={() => setAuthOpen(true)} />
        <div className="max-w-md mx-auto px-4 py-20 text-center">
          <Store className="w-12 h-12 text-[#556B2F] mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-[#5A2D1D] mb-2">Store Billing</h1>
          <p className="text-sm text-[#2E1A12]/70 mb-6">
            Sign in with the user ID and password shared by your admin to bill walk-in customers.
          </p>
          <button
            type="button"
            onClick={() => setAuthOpen(true)}
            className="bg-[#556B2F] hover:bg-[#6a8438] text-[#F5E6CC] px-8 py-3 rounded-full font-bold"
          >
            Sign In
          </button>
        </div>
        <AuthModal
          open={authOpen}
          onOpenChange={setAuthOpen}
          storeOnly
          onStoreSignIn={() => navigate("/store", { replace: true })}
        />
        <Toaster {...TOASTER_OPTS} />
      </div>
    );
  }

  if (!isStoreAssociate) {
    return (
      <div className="min-h-screen bg-[#F5E6CC] grid place-items-center px-4">
        <div className="max-w-sm text-center bg-white/50 border border-[#5A2D1D]/20 rounded-sm p-8">
          <p className="font-display text-lg font-bold text-[#5A2D1D] mb-2">Store access only</p>
          <p className="text-sm text-[#2E1A12]/70 mb-5">
            This login is not a store associate account. Use the credentials your admin gave you for store billing.
          </p>
          <button
            type="button"
            onClick={signOut}
            className="bg-[#5A2D1D] text-[#F5E6CC] px-6 py-2.5 rounded-full font-semibold text-sm"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5E6CC]">
      <StoreBar onAuthOpen={() => setAuthOpen(true)} />
      <main className="py-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Routes>
          <Route path="/store" element={<StoreBilling />} />
          <Route path="*" element={<Navigate to="/store" replace />} />
        </Routes>
      </main>
      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        storeOnly
        onStoreSignIn={() => navigate("/store", { replace: true })}
      />
      <Toaster {...TOASTER_OPTS} offset={{ top: "60px" }} />
    </div>
  );
};
